import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Handshake, CheckCircle2, Send, Loader2, Building2, Briefcase, GraduationCap, Megaphone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  { id: "recruitment", label: "Recrutement", desc: "Stages, alternance, premiers emplois", icon: Briefcase },
  { id: "academic", label: "Partenariat académique", desc: "Projets, interventions, R&D", icon: GraduationCap },
  { id: "events", label: "Événements & visibilité", desc: "Sponsoring, conférences, salons", icon: Megaphone },
  { id: "general", label: "Autre", desc: "Je préfère en discuter", icon: Building2 },
];

export const PartnerLeadForm = ({ compact = false }) => {
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "", partnership_type: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.partnership_type) { toast.error("Choisissez un type de partenariat"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/partners`, form);
      setSuccess(true);
      toast.success("Demande envoyée !", { description: "Notre équipe vous recontacte sous 48h." });
    } catch (err) {
      toast.error("Erreur", { description: err?.response?.data?.detail?.[0]?.msg || "Veuillez réessayer." });
    } finally { setLoading(false); }
  };

  if (success) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white border border-navy-100 p-10 text-center"
        data-testid="partner-form-success"
      >
        <motion.div
          initial={{ scale: 0, rotate: -90 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 180 }}
          className="w-14 h-14 bg-navy-500 text-white flex items-center justify-center mx-auto mb-5"
        >
          <CheckCircle2 className="h-7 w-7" />
        </motion.div>
        <div className="overline mb-3">Demande reçue</div>
        <h3 className="font-display text-2xl md:text-3xl text-navy-700 mb-3">Merci, {form.name.split(" ")[0]}.</h3>
        <p className="text-[#555]">
          Notre équipe partenariats vous recontacte à <span className="text-navy-700 font-semibold">{form.email}</span> sous 48 heures.
        </p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={onSubmit} data-testid="partner-form" className={`bg-white border border-navy-100 ${compact ? "p-6" : "p-8 md:p-10"}`}>
      <div className="flex items-center gap-3 mb-7">
        <Handshake className="h-6 w-6 text-navy-500" strokeWidth={1.6} />
        <h3 className="font-display text-2xl md:text-3xl text-navy-700 leading-tight">
          Devenir partenaire
        </h3>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-7">
        {TYPES.map((t) => {
          const Icon = t.icon;
          const active = form.partnership_type === t.id;
          return (
            <button
              type="button"
              key={t.id}
              onClick={() => setForm({ ...form, partnership_type: t.id })}
              data-testid={`partner-type-${t.id}`}
              className={`p-3 border text-left transition-all flex flex-col gap-1.5 ${
                active ? "border-navy-500 bg-navy-50" : "border-navy-100 hover:border-navy-300"
              }`}
            >
              <Icon className={`h-4 w-4 ${active ? "text-navy-500" : "text-navy-400"}`} strokeWidth={1.6} />
              <div className={`text-[12px] font-semibold leading-tight ${active ? "text-navy-700" : "text-navy-700"}`}>
                {t.label}
              </div>
              <div className="text-[10px] text-[#888] hidden md:block">{t.desc}</div>
            </button>
          );
        })}
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <Label htmlFor="p-name" className="text-navy-700 text-[11px] uppercase tracking-[0.18em] font-semibold">Nom complet *</Label>
          <Input id="p-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="mt-2 rounded-none border-0 border-b border-navy-100 px-0 h-11 focus-visible:ring-0 focus-visible:border-navy-500"
            data-testid="partner-name" />
        </div>
        <div>
          <Label htmlFor="p-company" className="text-navy-700 text-[11px] uppercase tracking-[0.18em] font-semibold">Société *</Label>
          <Input id="p-company" required value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
            className="mt-2 rounded-none border-0 border-b border-navy-100 px-0 h-11 focus-visible:ring-0 focus-visible:border-navy-500"
            data-testid="partner-company" />
        </div>
        <div>
          <Label htmlFor="p-email" className="text-navy-700 text-[11px] uppercase tracking-[0.18em] font-semibold">Email pro *</Label>
          <Input id="p-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="mt-2 rounded-none border-0 border-b border-navy-100 px-0 h-11 focus-visible:ring-0 focus-visible:border-navy-500"
            data-testid="partner-email" />
        </div>
        <div>
          <Label htmlFor="p-phone" className="text-navy-700 text-[11px] uppercase tracking-[0.18em] font-semibold">Téléphone</Label>
          <Input id="p-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="+225 ..."
            className="mt-2 rounded-none border-0 border-b border-navy-100 px-0 h-11 focus-visible:ring-0 focus-visible:border-navy-500"
            data-testid="partner-phone" />
        </div>
      </div>

      <div className="mt-5">
        <Label htmlFor="p-msg" className="text-navy-700 text-[11px] uppercase tracking-[0.18em] font-semibold">Votre projet *</Label>
        <Textarea id="p-msg" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
          placeholder="Décrivez votre besoin, vos attentes, le timing..."
          className="mt-2 rounded-none border-navy-100 focus-visible:ring-navy-500 focus-visible:ring-offset-0 focus-visible:border-navy-500 resize-none"
          data-testid="partner-message" />
      </div>

      <button type="submit" disabled={loading} className="btn-red mt-7 disabled:opacity-60" data-testid="partner-submit">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {loading ? "Envoi…" : "Envoyer la demande"}
      </button>
    </form>
  );
};

export default PartnerLeadForm;
